import { useMemo, useState } from "react";
import { ChevronRight, ChevronDown, Sparkles, User, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fi } from "date-fns/locale";

/**
 * FieldStartedInbox — "Kentällä aloitetut työt"
 *
 * Nostaa owner-Dashboardin yläreunaan raportit joita KENTTÄKÄYTTÄJÄ (member)
 * on aloittanut /kentta-näkymästä. Tämä on inbox-tyylinen esiin nosto — kaikki
 * samat raportit näkyvät myös normaalissa listassa alempana, mutta tässä ne
 * korostetaan kunnes pääkäyttäjä on nähnyt ne.
 *
 * Tunnistus: raportilla on `created_by_role === "member"` (uusi kenttä
 * `/api/reports` -vastauksessa) TAI `user_id !== currentUserId` (varmistus
 * niille yrityksille joissa on jo ollut jäseniä ennen roolin lisäystä).
 */
export default function FieldStartedInbox({ reports, currentUserId, onOpen }) {
  const [open, setOpen] = useState(true);
  const fieldReports = useMemo(() => {
    if (!Array.isArray(reports) || !currentUserId) return [];
    const now = Date.now();
    const cutoff = now - 30 * 24 * 60 * 60 * 1000; // 30 päivää
    return reports.filter((r) => {
      const byRole = r.created_by_role === "member";
      const byId = r.user_id && r.user_id !== currentUserId;
      if (!(byRole || byId)) return false;
      const ts = (() => {
        try { return new Date(r.created_at).getTime(); } catch { return now; }
      })();
      return ts >= cutoff;
    }).slice(0, 8);
  }, [reports, currentUserId]);

  if (fieldReports.length === 0) return null;

  return (
    <section className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50/40 overflow-hidden" data-testid="field-started-inbox">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 md:px-5 py-3.5 text-left hover:bg-emerald-50/70"
        data-testid="field-started-toggle"
      >
        <div className="h-9 w-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 shadow-sm shadow-emerald-500/20">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">Kentällä aloitetut työt</div>
          <div className="text-sm text-slate-700 mt-0.5">
            {fieldReports.length} {fieldReports.length === 1 ? "työ" : "työtä"} · asentaja aloittanut viimeisen 30 päivän aikana
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-emerald-700 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <ul className="divide-y divide-emerald-100 bg-white/50 border-t border-emerald-100">
          {fieldReports.map((r) => {
            let rel = "";
            try { rel = formatDistanceToNow(new Date(r.created_at), { addSuffix: true, locale: fi }); } catch { /* noop */ }
            return (
              <li key={r.report_id}>
                <button
                  type="button"
                  onClick={() => onOpen?.(r)}
                  className="w-full text-left flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-white transition-colors"
                  data-testid={`field-started-item-${r.report_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{r.title || r.work_type || "Työ"}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      {r.created_by_name && (
                        <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{r.created_by_name}</span>
                      )}
                      {rel && (
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{rel}</span>
                      )}
                      {r.unit_count ? <span>· {r.unit_count} yks.</span> : null}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
